function ensureApiBase(apiEndpoint) {
  if (!apiEndpoint) return "";
  const trimmed = apiEndpoint.trim();
  if (!trimmed) return "";
  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
}

const OAUTH_DRAFT_KEY = "aid-planner-actionstep-oauth-draft";
const ACTIONSTEP_UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024;
const ACTIONSTEP_BLOCKED_EXTENSIONS = new Set([
  "com", "sh", "exe", "bat", "cmd", "vbs", "src", "msi", "jar", "dll", "tpl",
]);

// Staging is the default. Connecting to live Actionstep - real client matters - has to be
// a deliberate choice, not what happens when nobody thought about it.
export const DEFAULT_ACTIONSTEP_ENVIRONMENT = "staging";

// Actionstep scopes name the resources being accessed - they are not "read"/"write"
// verbs, and sending those produces "Unknown scope [read]" at the authorize step.
//
// Each entry corresponds to a REST path this module calls:
//   actions          -> rest/actions          (matter search)
//   filenotes        -> rest/filenotes        (save a file note)
//   files            -> rest/files            (chunked document upload)
//   actiondocuments  -> rest/actiondocuments  (attach the upload to a matter)
//   actionfolders    -> rest/actionfolders    (find the folder to file it into)
//   tasks            -> rest/tasks
//
// These must also be enabled on the Actionstep application itself. Actionstep grants
// scopes per registered app, so requesting one that was not enabled fails at authorize
// even though the string is valid. See docs/ACTIONSTEP_TESTING.md.
export const ACTIONSTEP_SCOPES = [
  "actions",
  "filenotes",
  "files",
  "actiondocuments",
  "actionfolders",
  "tasks",
].join(" ");

export const ACTIONSTEP_ENVIRONMENTS = {
  production: {
    authorizeUrl: "https://go.actionstep.com/api/oauth/authorize",
    tokenUrl: "https://api.actionstep.com/api/oauth/token",
  },
  staging: {
    authorizeUrl: "https://go.actionstepstaging.com/api/oauth/authorize",
    tokenUrl: "https://api.actionstepstaging.com/api/oauth/token",
  },
};

// Actionstep answers with 204 No Content and an empty body in ordinary cases - a search
// that matched nothing, a write that returns no representation. response.json() on an
// empty body throws "Unexpected end of JSON input", so a search with no results surfaced
// to the user as a parse error rather than "no matters found".
//
// Returns null for an empty body. getArrayPayload(null) yields [], so callers reading a
// list need no change; callers reading an object should treat null as "nothing returned".
async function readActionstepJson(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Actionstep returned a response that could not be read as JSON.");
  }
}

// Actionstep returns a collection of one as a bare object, not a one-element array:
//   two matches -> { "actions": [ {...}, {...} ] }
//   one  match  -> { "actions": {...} }
// Requiring an array therefore discarded every search that matched exactly one matter,
// which reported as "no matters found" - indistinguishable from no match at all, and the
// most likely outcome when searching a specific client or matter number.
function toResultArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
}

// Envelope keys Actionstep sends alongside the payload. Without skipping these, the
// fallback below can return `linked` or `meta` and produce confident nonsense.
const ACTIONSTEP_ENVELOPE_KEYS = new Set(["meta", "links", "linked"]);

function getArrayPayload(json) {
  if (json?.actions !== undefined) return toResultArray(json.actions);
  if (json?.data !== undefined) return toResultArray(json.data);

  for (const [key, value] of Object.entries(json || {})) {
    if (ACTIONSTEP_ENVELOPE_KEYS.has(key)) continue;
    if (Array.isArray(value)) return value;
  }
  return [];
}

function getLinkedName(record, key) {
  const value = record?.[key];
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.displayName || value.name || value.label || "";
}

function getLinkedId(record, key) {
  const value = record?.[key];
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return value.id ? String(value.id) : "";
}

function firstString(record, keys) {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function normalizeDate(value) {
  if (!value || typeof value !== "string") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function splitFullName(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) {
    return { firstName: "", lastName: "", fullName: "" };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "", fullName: trimmed };
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
    fullName: trimmed,
  };
}

function inferMatterType(...values) {
  const text = values.filter(Boolean).join(" ").toLowerCase();
  if (!text) return "";
  if (text.includes("family violence")) return "Family Violence";
  if (text.includes("criminal") || text.includes("bail") || text.includes("plea") || text.includes("sentence") || text.includes("committal") || text.includes("appeal")) {
    return "Criminal";
  }
  if (text.includes("family")) return "Family";
  if (text.includes("civil")) return "Civil";
  return "";
}

function inferCourt(...values) {
  const text = values.filter(Boolean).join(" ").toLowerCase();
  if (!text) return "";
  if (text.includes("children")) return "Children's Court";
  if (text.includes("magistrates")) return "Magistrates' Court";
  if (text.includes("county")) return "County Court";
  if (text.includes("supreme")) return "Supreme Court";
  if (text.includes("family court")) return "Family Court";
  if (text.includes("federal circuit") || text.includes("fcfcoa")) return "Federal Circuit and Family Court";
  return "";
}

function inferAppearanceType(...values) {
  const text = values.filter(Boolean).join(" ").toLowerCase();
  if (!text) return "";
  if (text.includes("contest mention")) return "Contest Mention";
  if (text.includes("bail")) return "Bail Application";
  if (text.includes("committal")) return "Committal";
  if (text.includes("sentence")) return "Sentence";
  if (text.includes("plea")) return "Plea";
  if (text.includes("appeal")) return "Appeal";
  if (text.includes("trial")) return "Trial";
  if (text.includes("conference")) return "Conference";
  if (text.includes("mention")) return "Mention";
  return "";
}

function uniqueMatters(matters) {
  const seen = new Set();
  return matters.filter((matter) => {
    const key = matter.externalId || `${matter.client.fileNumber}|${matter.client.fullName}|${matter.matter.summary}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function mapActionstepMatter(record) {
  const summary = firstString(record, ["name", "displayName", "title", "description"]);
  const clientName = firstString(record, ["clientName", "contactName", "displayName", "name", "title"]);
  const parsedClient = splitFullName(clientName);

  const reference =
    firstString(record, ["reference", "number", "fileNumber", "code", "displayCode"]) ||
    (record?.id ? `AS-${record.id}` : "");

  const actionTypeName = getLinkedName(record, "actionType");
  const responsibleLawyer = getLinkedName(record, "assignedTo") || getLinkedName(record, "owner");
  const rateName = getLinkedName(record, "rate");
  const courtName = firstString(record, ["courtName", "court"]) || inferCourt(actionTypeName, summary);
  const appearanceType = inferAppearanceType(actionTypeName, summary);
  const nextAppearanceDate = normalizeDate(
    firstString(record, [
      "nextAppearanceDate",
      "nextCourtDate",
      "startDate",
      "startTimestamp",
      "createdAt",
      "createdTimestamp",
      "modifiedAt",
      "modifiedTimestamp",
    ])
  );

  const matterType = /criminal/i.test(actionTypeName)
    ? "Criminal"
    : inferMatterType(actionTypeName, summary, courtName, appearanceType);

  const safeId = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
  return {
    externalId: String(record?.id || reference || safeId),
    actionstep: {
      assignedToId: getLinkedId(record, "assignedTo") || getLinkedId(record, "owner"),
      actionTypeId: getLinkedId(record, "actionType"),
      rateId: getLinkedId(record, "rate"),
      rateName,
      importedReview: {
        inferredMatterType: matterType,
        inferredCourt: courtName,
        inferredAppearanceType: appearanceType,
      },
    },
    client: {
      firstName: parsedClient.firstName,
      lastName: parsedClient.lastName,
      fullName: parsedClient.fullName || summary,
      fileNumber: reference,
    },
    matter: {
      matterType,
      court: courtName || actionTypeName,
      appearanceType,
      nextAppearanceDate,
      lawyer: responsibleLawyer,
      summary: summary || "Imported from Actionstep matter search.",
    },
  };
}

async function fetchActionstepSearch(url, accessToken) {
  const response = await fetch(url.toString(), {
    headers: {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
      Authorization: `Bearer ${accessToken.trim()}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Actionstep search failed (${response.status}). ${errorText.slice(0, 240)}`.trim());
  }

  const json = await readActionstepJson(response);
  return getArrayPayload(json).map(mapActionstepMatter);
}

export async function searchActionstepMatters({ apiEndpoint, accessToken, query }) {
  const baseUrl = ensureApiBase(apiEndpoint);
  if (!baseUrl) {
    throw new Error("Actionstep API endpoint is required.");
  }

  if (!accessToken?.trim()) {
    throw new Error("Actionstep access token is required.");
  }

  const searchTerm = query?.trim();
  if (!searchTerm) {
    throw new Error("Enter a matter search query first.");
  }

  const searchConfigs = [
    { name_ilike: `*${searchTerm}*` },
    { reference_ilike: `*${searchTerm}*` },
    { filter: `name ilike '%${searchTerm.replace(/'/g, "''")}%' OR reference ilike '%${searchTerm.replace(/'/g, "''")}%'` },
  ];

  const allResults = [];

  for (const filters of searchConfigs) {
    try {
      const url = new URL("rest/actions", baseUrl);
      url.searchParams.set("pageSize", "25");
      url.searchParams.set("include", "actionType,assignedTo,owner,rate");
      url.searchParams.set("fields[actions]", ":default,reference,number,fileNumber,code,displayCode,name,displayName,title,description,startDate,startTimestamp,createdAt,createdTimestamp,modifiedAt,modifiedTimestamp");
      url.searchParams.set("fields[actiontypes]", ":default,displayName,name");
      url.searchParams.set("fields[participants]", ":default,displayName,firstName,lastName");
      url.searchParams.set("fields[rates]", ":default,displayName,name");

      Object.entries(filters).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });

      const results = await fetchActionstepSearch(url, accessToken);
      allResults.push(...results);
    } catch (error) {
      if (allResults.length === 0) {
        throw error;
      }
    }
  }

  return uniqueMatters(allResults);
}

export async function listAllActionstepMatters({ apiEndpoint, accessToken }) {
  const baseUrl = ensureApiBase(apiEndpoint);
  if (!baseUrl) throw new Error("Actionstep API endpoint is required.");
  if (!accessToken?.trim()) throw new Error("Actionstep access token is required.");

  const url = new URL("rest/actions", baseUrl);
  url.searchParams.set("pageSize", "100");
  url.searchParams.set("include", "actionType,assignedTo,owner,rate");
  url.searchParams.set("fields[actions]", ":default,reference,number,fileNumber,code,displayCode,name,displayName,title,description,startDate,startTimestamp,createdAt,createdTimestamp,modifiedAt,modifiedTimestamp");
  url.searchParams.set("fields[actiontypes]", ":default,displayName,name");
  url.searchParams.set("fields[participants]", ":default,displayName,firstName,lastName");
  url.searchParams.set("fields[rates]", ":default,displayName,name");

  return fetchActionstepSearch(url, accessToken);
}

export function saveActionstepOAuthDraft(draft) {
  window.sessionStorage.setItem(OAUTH_DRAFT_KEY, JSON.stringify(draft));
}

export function loadActionstepOAuthDraft() {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearActionstepOAuthDraft() {
  window.sessionStorage.removeItem(OAUTH_DRAFT_KEY);
}

// Resolves an environment name to its endpoints, refusing anything it does not recognise.
//
// This previously fell back to production whenever the value was missing, misspelled, or
// lost between the authorize step and the token exchange - so a mistake silently connected
// to live Actionstep with no error and nothing on screen to say so. For an integration that
// reads real client matters, the safe direction to fail is closed.
export function resolveActionstepEnvironment(environment) {
  const name = String(environment ?? "").trim();
  if (!name) {
    throw new Error(
      "No Actionstep environment was specified. Choose Production or Staging before connecting.",
    );
  }
  const config = ACTIONSTEP_ENVIRONMENTS[name];
  if (!config) {
    throw new Error(
      `Unknown Actionstep environment "${name}". Expected one of: ${Object.keys(ACTIONSTEP_ENVIRONMENTS).join(", ")}.`,
    );
  }
  return config;
}

export function buildActionstepAuthorizeUrl({
  environment,
  clientId,
  scopes,
  redirectUri,
}) {
  const config = resolveActionstepEnvironment(environment);
  const url = new URL(config.authorizeUrl);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId.trim());
  url.searchParams.set("scope", scopes.trim());
  url.searchParams.set("redirect_uri", redirectUri.trim());
  return url.toString();
}

export async function exchangeActionstepCode({
  environment,
  clientId,
  clientSecret,
  redirectUri,
  code,
}) {
  const config = resolveActionstepEnvironment(environment);
  const body = new URLSearchParams({
    code: code.trim(),
    client_id: clientId.trim(),
    client_secret: clientSecret.trim(),
    grant_type: "authorization_code",
    redirect_uri: redirectUri.trim(),
  });

  const response = await fetch(config.tokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Actionstep token exchange failed (${response.status}). ${errorText.slice(0, 240)}`.trim());
  }

  return readActionstepJson(response);
}

// `source` on an Actionstep file note is the note's type, not a free-text label for who
// wrote it. Sending "Aid Planner" was rejected:
//
//   400 ValidationError - "Access denied, only User notes can be created or modified"
//
// System-generated note types are read-only over the API, so anything the portal writes
// must be a User note. Attribution belongs in the note text, which every caller already
// includes as a header line.
const ACTIONSTEP_USER_NOTE = "User";

// Actionstep access tokens are short-lived. The authorization response carries a refresh
// token alongside them, and until now that was thrown away - so the first expiry ended the
// session and the practitioner had to sign in again mid-task, losing whatever they were
// part-way through saving.
export async function refreshActionstepToken({ environment, clientId, clientSecret, refreshToken }) {
  if (!refreshToken?.trim()) throw new Error("No Actionstep refresh token is available.");
  const config = resolveActionstepEnvironment(environment);

  const response = await fetch(config.tokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken.trim(),
      client_id: String(clientId || "").trim(),
      client_secret: String(clientSecret || "").trim(),
    }).toString(),
  });

  if (!response.ok) {
    // Deliberately no response body: a rejected refresh can echo the client secret back.
    throw new Error(`Actionstep refused the token refresh (${response.status}).`);
  }
  return readActionstepJson(response);
}

// True when a failure is "this token is stale" rather than something worth surfacing.
// Actionstep answers 401 with error=expired_token; matching the code as well as the status
// avoids retrying a genuine authorisation failure, which would just fail twice.
export function isActionstepTokenExpired(error) {
  const message = error instanceof Error ? error.message : String(error || "");
  return message.includes("401") && /expired_token|token provided has expired/i.test(message);
}

export async function createActionstepFileNote({
  apiEndpoint,
  accessToken,
  actionId,
  text,
}) {
  const baseUrl = ensureApiBase(apiEndpoint);
  if (!baseUrl) throw new Error("Actionstep API endpoint is required.");
  if (!accessToken?.trim()) throw new Error("Actionstep access token is required.");
  if (!actionId) throw new Error("Actionstep matter ID is required.");
  if (!text?.trim()) throw new Error("A file note message is required.");

  const url = new URL("rest/filenotes", baseUrl);
  const payload = {
    filenotes: {
      text: text.trim(),
      source: ACTIONSTEP_USER_NOTE,
      noteTimestamp: new Date().toISOString(),
      links: {
        action: String(actionId),
      },
    },
  };

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
      Authorization: `Bearer ${accessToken.trim()}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Actionstep file note failed (${response.status}). ${errorText.slice(0, 240)}`.trim());
  }

  return readActionstepJson(response);
}

export function buildActionstepDocumentPayload(actionId, fileId, fileName, folderId = null) {
  if (!actionId) throw new Error("Actionstep matter ID is required.");
  if (!fileId?.trim()) throw new Error("Actionstep file identifier is required.");
  if (!fileName?.trim()) throw new Error("A file name is required.");

  const links = { action: String(actionId) };
  if (folderId) links.actionFolder = String(folderId);

  return {
    actiondocuments: {
      name: fileName.trim(),
      file: `${fileId.trim()};${fileName.trim()}`,
      links,
    },
  };
}

// Finds a matter folder by name, so documents can be filed where the firm expects them
// rather than loose at the root of the matter.
//
// Returns null when the folder does not exist or cannot be read. Callers treat that as
// "file it unfiled" rather than failing the upload: losing the document because a folder
// was renamed would be a far worse outcome than filing it in the wrong place.
export async function findActionstepFolderId({ apiEndpoint, accessToken, actionId, folderName }) {
  const result = await resolveActionstepFolder({ apiEndpoint, accessToken, actionId, folderName });
  return result.id;
}

// Resolves a folder and says what happened, rather than only whether it worked.
//
// The first version returned null for every failure - not configured, request refused,
// folder absent, name mismatched - so a document filed at the matter root gave no clue
// which. Silent misfiling is worse than a visible failure: nobody notices until they go
// looking for a document that is not where they expect it.
//
// Returns { id, reason, available } where `available` lists the folder names the matter
// actually has, so a mismatch names the alternatives instead of leaving them to be
// guessed at.
export async function resolveActionstepFolder({ apiEndpoint, accessToken, actionId, folderName }) {
  const baseUrl = ensureApiBase(apiEndpoint);
  if (!baseUrl || !accessToken?.trim() || !actionId) {
    return { id: null, reason: "not-configured", available: [] };
  }
  if (!folderName?.trim()) return { id: null, reason: "no-folder-requested", available: [] };

  let response;
  try {
    const url = new URL("rest/actionfolders", baseUrl);
    // Actionstep filters are `field` plus an operator suffix. Both spellings are sent:
    // an unrecognised parameter is ignored rather than rejected, and which one this
    // resource accepts is not documented.
    url.searchParams.set("action", String(actionId));
    url.searchParams.set("action_eq", String(actionId));
    response = await fetch(url.toString(), {
      headers: {
        Accept: "application/vnd.api+json",
        Authorization: `Bearer ${accessToken.trim()}`,
      },
    });
  } catch {
    return { id: null, reason: "request-failed", available: [] };
  }

  if (!response.ok) {
    // 401/403 here almost always means the `actionfolders` scope was not granted on the
    // Actionstep application, which is a configuration answer rather than a code one.
    return { id: null, reason: `refused-${response.status}`, available: [] };
  }

  let json;
  try {
    json = await readActionstepJson(response);
  } catch {
    return { id: null, reason: "unreadable-response", available: [] };
  }

  const folders = json?.actionfolders !== undefined
    ? toResultArray(json.actionfolders)
    : getArrayPayload(json);

  const nameOf = (folder) =>
    String(folder?.name ?? folder?.folderName ?? folder?.displayName ?? "").trim();
  const available = folders.map(nameOf).filter(Boolean);

  if (!folders.length) return { id: null, reason: "no-folders-returned", available };

  const wanted = folderName.trim().toLowerCase();
  const match = folders.find((folder) => nameOf(folder).toLowerCase() === wanted);
  if (!match) return { id: null, reason: "folder-not-found", available };
  if (!match.id) return { id: null, reason: "folder-has-no-id", available };

  return { id: String(match.id), reason: "ok", available };
}

export async function uploadActionstepMatterDocument({
  apiEndpoint,
  accessToken,
  actionId,
  file,
  folderName = "",
}) {
  const baseUrl = ensureApiBase(apiEndpoint);
  if (!baseUrl) throw new Error("Actionstep API endpoint is required.");
  if (!accessToken?.trim()) throw new Error("Actionstep access token is required.");
  if (!actionId) throw new Error("Actionstep matter ID is required.");
  if (!file?.name || typeof file.slice !== "function") throw new Error("Choose a file to upload.");
  if (!file.size) throw new Error("The selected file is empty.");

  const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
  if (ACTIONSTEP_BLOCKED_EXTENSIONS.has(extension)) {
    throw new Error(`Actionstep does not allow .${extension} files.`);
  }

  const partCount = Math.ceil(file.size / ACTIONSTEP_UPLOAD_CHUNK_SIZE);
  let fileId = "";
  for (let index = 0; index < partCount; index += 1) {
    const start = index * ACTIONSTEP_UPLOAD_CHUNK_SIZE;
    const chunk = file.slice(start, Math.min(start + ACTIONSTEP_UPLOAD_CHUNK_SIZE, file.size));
    const uploadPath = fileId ? `rest/files/${encodeURIComponent(fileId)}` : "rest/files";
    const uploadUrl = new URL(uploadPath, baseUrl);
    uploadUrl.searchParams.set("part_number", String(index + 1));
    uploadUrl.searchParams.set("part_count", String(partCount));
    const formData = new FormData();
    formData.append("file", chunk, file.name);

    const response = await fetch(uploadUrl.toString(), {
      method: "POST",
      headers: {
        Accept: "application/vnd.api+json",
        Authorization: `Bearer ${accessToken.trim()}`,
      },
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Actionstep file upload failed (${response.status}). ${errorText.slice(0, 240)}`.trim());
    }
    const json = await readActionstepJson(response);
    fileId = String(json?.files?.id || fileId);
    if (!fileId) throw new Error("Actionstep did not return a file identifier.");
  }

  // Best effort: an unresolvable folder files the document at the matter root rather than
  // failing the upload. Losing a document because a folder was renamed would be far worse
  // than filing it in the wrong place - but it is reported rather than silent, because a
  // document nobody can find is barely better than one that never arrived.
  const folder = folderName
    ? await resolveActionstepFolder({ apiEndpoint, accessToken, actionId, folderName })
    : { id: null, reason: "no-folder-requested", available: [] };
  const folderId = folder.id;

  const documentUrl = new URL("rest/actiondocuments", baseUrl);
  const response = await fetch(documentUrl.toString(), {
    method: "POST",
    headers: {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
      Authorization: `Bearer ${accessToken.trim()}`,
    },
    body: JSON.stringify(buildActionstepDocumentPayload(actionId, fileId, file.name, folderId)),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Actionstep document link failed (${response.status}). ${errorText.slice(0, 240)}`.trim());
  }
  const document = await readActionstepJson(response);
  // The caller needs to know where it landed, not only that it arrived.
  return { document, folder };
}

export async function createActionstepTask({
  apiEndpoint,
  accessToken,
  actionId,
  name,
  assigneeId,
  rateId,
  dueDate,
  priority = "Normal",
}) {
  const baseUrl = ensureApiBase(apiEndpoint);
  if (!baseUrl) throw new Error("Actionstep API endpoint is required.");
  if (!accessToken?.trim()) throw new Error("Actionstep access token is required.");
  if (!actionId) throw new Error("Actionstep matter ID is required.");
  if (!name?.trim()) throw new Error("Task title is required.");
  if (!assigneeId?.trim()) throw new Error("Actionstep assignee ID is required.");
  if (!rateId?.trim()) throw new Error("Actionstep rate ID is required.");

  const url = new URL("rest/tasks", baseUrl);
  const task = {
    name: name.trim(),
    status: "Incomplete",
    priority,
    links: {
      action: String(actionId),
      assignee: assigneeId.trim(),
      rate: rateId.trim(),
    },
  };

  if (dueDate?.trim()) {
    task.dueDate = dueDate.trim();
  }

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
      Authorization: `Bearer ${accessToken.trim()}`,
    },
    body: JSON.stringify({ tasks: task }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Actionstep task creation failed (${response.status}). ${errorText.slice(0, 240)}`.trim());
  }

  return readActionstepJson(response);
}
